// „Meine Meldungen“ (Screen mieter/02): eigene, über das Portal gemeldete Schäden und Anfragen (#564) mit
// 4-Stufen-Status und Nachrichten an die Hausverwaltung.
import { useState } from 'react';
import { CalendarCheck, CaretDown, ChatCircleDots, ChatCircleText, CheckCircle, Clock, Wrench } from '@phosphor-icons/react';
import NachrichtenVerlauf from '../../components/portal/NachrichtenVerlauf';
import { ladeNachrichten, sendeNachricht } from '../../lib/portal/nachrichtenApi';
import { IconChip, LeerZustand, SeitenTitel, SegmentSteuerung, farbKlasse } from '../../components/portal/bausteine';
import { STUFEN, kategorieAusTitel, meldungMeta, meldungStufe, meldungUpdate, type Meldung, type Stufe } from '../../lib/portal/mieter';
import { anfrageBeantwortet, anfrageStatusText } from '../../lib/portal/einsaetze';
import { KATEGORIE_STIL } from './symbole';

export function StufenChip({ stufe }: { stufe: Stufe }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 800, padding: '4px 9px', borderRadius: 10, whiteSpace: 'nowrap', flexShrink: 0,
      background: `var(--p-stufe-${stufe}-bg)`, color: `var(--p-stufe-${stufe})` }}>
      {STUFEN[stufe]}
    </span>
  );
}

/** Vier Balken; mit `beschriftet` stehen die Stufennamen darunter. */
export function StufenBalken({ stufe, beschriftet = false }: { stufe: Stufe; beschriftet?: boolean }) {
  return (
    <div role="img" aria-label={`Stand: ${STUFEN[stufe]} (${stufe + 1} von 4)`}
      style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: beschriftet ? 6 : 4, width: '100%' }}>
      {STUFEN.map((l, i) => (
        <div key={l} style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <span style={{ height: beschriftet ? 5 : 4, borderRadius: 3, background: i <= stufe ? `var(--p-stufe-${stufe})` : 'var(--p-w10)' }} />
          {beschriftet && <span style={{ fontSize: 10.5, fontWeight: 700, lineHeight: 1.2, color: i <= stufe ? 'var(--p-w80)' : 'var(--p-w35)' }}>{l}</span>}
        </div>
      ))}
    </div>
  );
}

const UPDATE_SYMBOL = { 0: Clock, 1: Wrench, 2: CalendarCheck, 3: CheckCircle } as const;

export function MeldungKarte({ m, neu }: { m: Meldung; neu: boolean }) {
  const stufe = meldungStufe(m);
  const anfrage = m.art === 'anfrage';
  const stil = anfrage ? { farbe: 'gemein' as const, icon: ChatCircleText } : KATEGORIE_STIL[kategorieAusTitel(m.titel)];
  const [nachrichtenOffen, setNachrichtenOffen] = useState(false);
  const US = UPDATE_SYMBOL[stufe];
  return (
    <article className={`p-glas p-hebt ${farbKlasse(stil.farbe)}`}
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14, boxShadow: neu ? 'var(--p-glas-sh), var(--p-gold-ring-leise)' : undefined }}>
      <div className="p-glas-schimmer"><stil.icon size={160} weight="duotone" className="p-wasserzeichen" style={{ opacity: 0.11 }} /></div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <IconChip farbe={stil.farbe} icon={stil.icon} groesse={40} radius={12} />
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 15.5, fontWeight: 800, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{m.titel}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-w50)' }}>
            {anfrage && <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 7px', borderRadius: 8, marginRight: 6, background: 'var(--p-ton-info-bg)', color: 'var(--p-ton-info-fg)' }}>Anfrage</span>}
            {meldungMeta(m)}
          </span>
        </span>
        {anfrage ? (
          <span style={{ fontSize: 11, fontWeight: 800, padding: '4px 9px', borderRadius: 10, whiteSpace: 'nowrap', flexShrink: 0,
            background: anfrageBeantwortet(m) ? 'var(--p-ton-ok-bg)' : 'var(--p-w08)', color: anfrageBeantwortet(m) ? 'var(--p-ton-ok-fg)' : 'var(--p-w75)' }}>
            {anfrageStatusText(m)}
          </span>
        ) : <StufenChip stufe={stufe} />}
      </div>
      {!anfrage && <StufenBalken stufe={stufe} beschriftet />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 14, background: 'var(--p-w05)', fontSize: 12.5, fontWeight: 700, color: 'var(--p-w75)' }}>
        <US size={15} weight="bold" style={{ color: `var(--p-stufe-${stufe})` }} />{anfrage ? (anfrageBeantwortet(m) ? 'Die Verwaltung hat geantwortet' : 'Anfrage gesendet – die Antwort kommt hier im Portal') : meldungUpdate(m)}
      </div>
      {m.beschreibung && (
        <span style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.5, color: 'var(--p-w60)', overflowWrap: 'anywhere' }}>„{m.beschreibung}“</span>
      )}
      <button type="button" onClick={() => setNachrichtenOffen((o) => !o)} aria-expanded={nachrichtenOffen}
        data-tip={nachrichtenOffen ? 'Nachrichten schließen' : 'Nachrichten ansehen oder eine Rückfrage stellen'}
        style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 4px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 800, color: 'var(--p-lila-link)', fontFamily: 'inherit' }}>
        <ChatCircleDots size={15} weight="fill" />
        {m.nachrichten ? `Nachrichten (${m.nachrichten})` : 'Rückfrage stellen'}
        <CaretDown size={12} weight="bold" style={{ transform: nachrichtenOffen ? 'rotate(180deg)' : undefined }} />
      </button>
      {nachrichtenOffen && <NachrichtenVerlauf ticketId={m.id} laden={ladeNachrichten} senden={sendeNachricht} />}
    </article>
  );
}

export type MeldungFilter = 'offen' | 'erledigt';

export default function MieterMeldungen({ meldungen, filter, onFilter, neuId, onAnfrage }: {
  meldungen: Meldung[]; filter: MeldungFilter; onFilter: (f: MeldungFilter) => void; neuId: string | null;
  /** #564: „Frage an die Verwaltung“ öffnen */
  onAnfrage?: () => void;
}) {
  const offen = meldungen.filter((m) => meldungStufe(m) < 3);
  const erledigt = meldungen.filter((m) => meldungStufe(m) === 3);
  const liste = filter === 'offen' ? offen : erledigt;
  return (
    <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SeitenTitel
        oben="Schäden und Anliegen"
        titel="Meine Meldungen"
        rechts={(
          <div style={{ minWidth: 230 }}>
            <SegmentSteuerung<MeldungFilter>
              label="Meldungen filtern" wert={filter} onWahl={onFilter} hoehe={32}
              optionen={[
                { key: 'offen', label: `Offen (${offen.length})`, tip: 'Noch nicht erledigte Meldungen' },
                { key: 'erledigt', label: `Erledigt (${erledigt.length})`, tip: 'Abgeschlossene Meldungen' },
              ]}
            />
          </div>
        )}
      />
      {onAnfrage && (
        <button type="button" className="p-knopf" onClick={onAnfrage} data-tip="Eine Frage an die Verwaltung schicken – die Antwort kommt hier im Portal"
          style={{ alignSelf: 'flex-start', height: 40, border: 'none', background: 'var(--p-w08)', fontSize: 13 }}>
          <ChatCircleText size={16} weight="bold" /> Frage an die Verwaltung
        </button>
      )}
      {liste.length === 0
        ? <LeerZustand icon={CheckCircle} text="Keine Meldungen in dieser Ansicht" />
        : <div className="mp-karten">{liste.map((m) => <MeldungKarte key={m.id} m={m} neu={m.id === neuId} />)}</div>}
    </div>
  );
}
